import escapeHtml from "escape-html";
import type { EChartsOption } from "echarts";
import {
  buildAxisLabelStyle,
  buildTooltipStyle,
  buildTooltipValueStyle,
  CHART_FONT_FAMILY,
  chartGridLineColor,
  CONTAIN_AXIS_LABELS,
  formatImpressionAxis,
  getChartTextColors,
  IMPRESSION_FORMATTER,
  PERCENT_FORMATTER,
} from "@/pages/CampaignOverviewPage/charts/chartHelpers";
import { getChartPalette } from "@/pages/CampaignOverviewPage/charts/chartPalette";
import {
  groupByCreativeSize,
  weightedCtr,
} from "@/pages/CampaignOverviewPage/utils/creativeGrouping";
import { TEXT } from "@/constants/text";
import type { PerformanceReportRow } from "@/types/reporting";

const BUBBLE_TEXT = TEXT.campaignOverview.topPerformers.sizeBubble;

// Spec v2: r = 4 + 20 × √(clicks ÷ maxClicks) at a 600px-wide plot.
const BUBBLE_MIN_RADIUS = 4;
const BUBBLE_RADIUS_RANGE = 20;
const BUBBLE_FILL_OPACITY = 0.55;
const BUBBLE_STROKE_WIDTH = 1.5;
const REFERENCE_LINE_DASH = [4, 4];
const LABEL_FONT_SIZE = 12;

export const bubbleRadius = (clicks: number, maxClicks: number): number =>
  BUBBLE_MIN_RADIUS +
  BUBBLE_RADIUS_RANGE * Math.sqrt(maxClicks > 0 ? clicks / maxClicks : 0);

interface Bubble {
  clicks: number;
  ctr: number;
  impressions: number;
  size: string;
}

// A log x-axis can't plot zero, so sizes with no impressions are dropped.
// Sorted largest first so small bubbles draw on top and stay visible.
export const buildBubbles = (rows: PerformanceReportRow[]): Bubble[] =>
  groupByCreativeSize(rows)
    .filter(({ impressions }) => impressions > 0)
    .map(({ clicks, impressions, size }) => ({
      clicks,
      ctr: weightedCtr(clicks, impressions),
      impressions,
      size,
    }))
    .sort((bubbleA, bubbleB) => bubbleB.clicks - bubbleA.clicks);

export const buildSizeBubbleOption = (
  rows: PerformanceReportRow[],
  isDark: boolean
): EChartsOption => {
  const axisLabelStyle = buildAxisLabelStyle(isDark);
  const { primary, secondary } = getChartTextColors(isDark);
  const valueStyle = buildTooltipValueStyle(isDark);
  const gridLineColor = chartGridLineColor(isDark);
  const palette = getChartPalette(isDark);
  const bubbles = buildBubbles(rows);
  const maxClicks = Math.max(0, ...bubbles.map(({ clicks }) => clicks));
  const totalClicks = bubbles.reduce((sum, { clicks }) => sum + clicks, 0);
  const totalImpressions = bubbles.reduce(
    (sum, { impressions }) => sum + impressions,
    0
  );
  const blendedCtr = weightedCtr(totalClicks, totalImpressions);

  // Only the notable points are labelled: the biggest by clicks (first, since
  // bubbles are sorted by clicks) and the one with the highest CTR.
  const topCtrSize = bubbles.reduce<Bubble | null>(
    (best, bubble) => (!best || bubble.ctr > best.ctr ? bubble : best),
    null
  )?.size;
  const labelledSizes = new Set([bubbles[0]?.size, topCtrSize]);

  const axisTitleStyle = {
    ...axisLabelStyle,
    color: secondary,
  };

  return {
    grid: { bottom: 56, left: 56, right: 24, top: 24, ...CONTAIN_AXIS_LABELS },
    series: [
      {
        data: bubbles.map(({ clicks, ctr, impressions, size }) => ({
          itemStyle: {
            borderColor: palette.primary,
            borderWidth: BUBBLE_STROKE_WIDTH,
            color: palette.primary,
            opacity: BUBBLE_FILL_OPACITY,
          },
          label: {
            color: primary,
            fontFamily: CHART_FONT_FAMILY,
            fontSize: LABEL_FONT_SIZE,
            fontWeight: 600,
            formatter: size,
            position: "right" as const,
            show: labelledSizes.has(size),
          },
          name: size,
          symbolSize: bubbleRadius(clicks, maxClicks) * 2,
          value: [impressions, ctr, clicks],
        })),
        markLine:
          bubbles.length > 0
            ? {
                animation: false,
                data: [{ yAxis: blendedCtr }],
                label: {
                  color: secondary,
                  fontFamily: CHART_FONT_FAMILY,
                  fontSize: LABEL_FONT_SIZE,
                  formatter: `${BUBBLE_TEXT.blended} ${PERCENT_FORMATTER.format(blendedCtr)}%`,
                  position: "insideStartTop" as const,
                },
                lineStyle: {
                  color: secondary,
                  type: REFERENCE_LINE_DASH,
                  width: 1,
                },
                silent: true,
                symbol: "none",
              }
            : undefined,
        type: "scatter",
      },
    ],
    tooltip: {
      ...buildTooltipStyle(isDark),
      formatter: (params: unknown) => {
        const {
          name,
          value: [impressions, ctr, clicks],
        } = params as { name: string; value: number[] };
        return [
          `<b style="${valueStyle}">${escapeHtml(name)}</b>`,
          `${BUBBLE_TEXT.impressions}: <b style="${valueStyle}">${IMPRESSION_FORMATTER.format(impressions)}</b>`,
          `${BUBBLE_TEXT.clicks}: <b style="${valueStyle}">${IMPRESSION_FORMATTER.format(clicks)}</b>`,
          `${BUBBLE_TEXT.ctr}: <b style="${valueStyle}">${PERCENT_FORMATTER.format(ctr)}%</b>`,
        ].join("<br/>");
      },
      trigger: "item",
    },
    xAxis: {
      axisLabel: { ...axisLabelStyle, formatter: formatImpressionAxis },
      name: BUBBLE_TEXT.xAxisTitle,
      nameGap: 32,
      nameLocation: "middle",
      nameTextStyle: axisTitleStyle,
      splitLine: { lineStyle: { color: gridLineColor } },
      type: "log",
    },
    yAxis: {
      axisLabel: {
        ...axisLabelStyle,
        formatter: (value: number) => `${value}%`,
      },
      min: 0,
      name: BUBBLE_TEXT.yAxisTitle,
      nameGap: 40,
      nameLocation: "middle",
      nameTextStyle: axisTitleStyle,
      splitLine: { lineStyle: { color: gridLineColor } },
      type: "value",
    },
  };
};
